import { useEffect, useState, useCallback, useRef, useMemo } from "preact/hooks";
import { lazy, Suspense } from "preact/compat";
import QRCode from "react-qr-code";
import { AnalyticsEvents } from "@murmurations/core";
// DigitalTwinView owns a Babylon engine + the flock graph. Loading it lazily
// keeps Babylon out of the microsite's entry chunk: the EnterSplash gate (and
// the bg-hero.webp underlay) cover the hero while the twin's chunk streams in,
// so first paint isn't blocked on parsing Babylon.
const DigitalTwinView = lazy(() => import("../components/DigitalTwinView").then((m) => ({ default: m.DigitalTwinView })));
import { EnterSplash } from "../components/EnterSplash";
import { LazyBoundary } from "../components/LazyBoundary";
import { IntroSequence } from "../components/IntroSequence";
import { RecordSection } from "./record/RecordSection";
import type { StoryQueue } from "../data/storyQueue";
import { OnboardingTooltip } from "../components/OnboardingTooltip";
import { useOnboarding, OnboardingStep } from "../hooks/useOnboarding";
import { MicIcon, QrIcon, CcIcon } from "../components/icons";
import Wordmark from "../assets/MURMURATIONS.svg";
import HandIcon from "../assets/icon-hand.svg?react";
import OpenQuote from "../assets/openquote.svg";
import CloseQuote from "../assets/closequote.svg";
// Edge-flock overlays, mapped to the Figma overlay groups by size/shape:
import BirdsTrio from "../assets/birds1.png"; //       2521:1299 (293×200, about right)
import BirdsLeftWide from "../assets/birds2.png"; //   2521:1554 (504×756, testimonials→resources, left)
import BirdsRightLarge from "../assets/birds3.png"; // 2521:1546 (728×906, about→testimonials, right)
import BirdsLeftTall from "../assets/birds4.png"; //   2521:1560 (568×852, hero→about, left)
import BirdsRightTall from "../assets/birds5.png"; //  2521:1529 (568×852, resources→credits, right)
import AgogLogo from "../assets/agog.svg";
import MusicCenterLogo from "../assets/musiccenter.svg";
import EyejackLogo from "../assets/eyejack.svg";
import ForFreedomsLogo from "../assets/Forfreedoms.svg";
import JanmLogo from "../assets/janm.svg";
import ChirlaLogo from "../assets/chirla.svg";
import CleanLogo from "../assets/clean.svg";
import GrandParkLogo from "../assets/gloriamolina.svg";
import IntroMusic from "../assets/intro.mp3";
import BgIntroVideo from "../assets/bg-intro.mp4";
// 1080 re-encode (1.9MB vs 4.8MB) — this is a decorative below-fold backdrop
// behind translucent bands, so the smaller variant is visually identical but
// holds far less resident decoded video on a 3GB device.
import BgCopyVideo from "../assets/bg-copy-1080.mp4";
import MurmurationVideo from "../assets/murmuration-web.mp4"; // web-optimised re-encode of murmuration.mp4
import CityHallPoster from "../assets/cityhall.webp";
import { PerfHud, perfHudEnabled } from "../components/PerfHud";
import { trackAnalyticsEvent } from "../analytics";

import "./microsite.scss";

// Self-report which build is live (read on-device via remote inspector or the
// ?debug PerfHud) so a "deployed but no change" report can be confirmed/ruled
// out at a glance.
if (typeof console !== "undefined") {
  console.info(`[murmurations] build ${typeof __BUILD_ID__ !== "undefined" ? __BUILD_ID__ : "dev"}`);
}

/**
 * Decode-gate a background <video> so it only holds an iOS hardware media
 * decoder while it's actually on-screen AND no bird story is playing. When
 * off-screen (or during story playback) we pause AND tear down its src
 * (removeAttribute + load()) so WebKit frees the decoder + buffered frames —
 * a plain pause() keeps the decoder resident. This is the fix for the iOS
 * digital-twin freeze: the below-fold videos (esp. the ms-about murmuration
 * loop, which had no off-screen pause) were decoding the whole time the user
 * was up in the hero, so when a story's audio decoder was also acquired the
 * iPhone XR (3GB) saturated its hardware decoders / WebContent memory and
 * hitched → heated → reload-crashed. CMS plays the same files fine because it
 * runs ONE audio element and nothing else. Render the <video> WITHOUT
 * src/autoplay — this hook owns both. `enabled` is false for reduced-motion.
 */
function useDecodeGatedVideo(
  videoRef: { current: HTMLVideoElement | null },
  src: string,
  storyActive: boolean,
  enabled: boolean,
  // What to watch for on-screen-ness. Defaults to the video element, but a
  // position:fixed backdrop video is always "intersecting", so its caller
  // passes the scrollable content wrapper (.ms-below-fold) instead.
  getObserveTarget?: () => Element | null,
): void {
  const onScreenRef = useRef(false);
  const stateRef = useRef({ storyActive, enabled, src });
  stateRef.current = { storyActive, enabled, src };

  const apply = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    const s = stateRef.current;
    const shouldPlay = s.enabled && onScreenRef.current && !s.storyActive;
    if (shouldPlay) {
      if (!v.getAttribute("src")) {
        v.setAttribute("src", s.src);
        v.load();
      }
      v.play()?.catch(() => {});
    } else {
      v.pause();
      if (v.getAttribute("src")) {
        v.removeAttribute("src");
        v.load(); // drop the hardware decoder + buffered frames, not just pause
      }
    }
  }, [videoRef]);

  // Re-apply when story/reduced-motion state changes.
  useEffect(() => {
    apply();
  }, [storyActive, enabled, apply]);

  // Observe on-screen state.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const target = getObserveTarget?.() ?? v;
    if (!target || typeof IntersectionObserver === "undefined") {
      onScreenRef.current = true;
      apply();
      return;
    }
    const io = new IntersectionObserver(([entry]) => {
      onScreenRef.current = entry.isIntersecting;
      apply();
    });
    io.observe(target);
    apply();
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoRef, apply]);
}

// Intro music levels: full under the IntroSequence, ducked to a quiet bed
// once the twin takes over, then dipped further still while a bird's story
// plays so the recorded voice sits clearly on top (sprint feedback: the
// ambient bed alone made some voices hard to hear). Intro duck runs across
// the intro's 3s crossfade; the story dip is a quick in/out around playback.
const INTRO_MUSIC_VOLUME = 0.7;
const AMBIENT_MUSIC_VOLUME = 0.12;
const FOCUSED_MUSIC_VOLUME = 0.04;
const MUSIC_DUCK_MS = 3000;
const STORY_DUCK_MS = 600;

interface MicrositeShellProps {
  /** Live story queue from the API, threaded into the digital twin viewer. */
  queue?: StoryQueue;
  /** Click handler for the "Launch in AR" CTA on mobile devices. */
  onLaunchAR: () => void;
}

/**
 * Microsite shell. The flock fills the first viewport (100dvh hero) with all
 * AR-style overlays anchored inside it; the page scrolls past the fold to
 * reveal editorial copy and a footer.
 *
 *   - Hero section: canvas + soft title overlay + floating CTAs + onboarding
 *     tooltip. Self-contained at 100dvh; everything anchored here scrolls
 *     away with the hero.
 *   - Top band (fixed): brand left, menu (hamburger) right — stays pinned
 *     while the user reads below-the-fold content.
 *   - Below the fold: about copy, pull quote, secondary record CTA, footer.
 *   - Floating AR FAB (bottom-right of hero): mobile = "Launch AR" button,
 *     desktop = QR popover.
 *   - Menu sheet (right slide-out): About / How it works / Record / Footer.
 *   - Record sheet: bottom sheet on touch, centered modal on pointer-fine.
 *
 * Styling lives in microsite.scss (.ms-* classes); only values computed at
 * runtime (tooltip anchors) stay inline.
 *
 * Hash contract: landing with `#record` (e.g. after AR-exit, see
 * App.tsx::handleArRecord) opens the record sheet on mount.
 */
export function MicrositeShell({ queue, onLaunchAR }: MicrositeShellProps) {
  const [isMobile, setIsMobile] = useState(false);
  // `?story=<id>` deeplink — e.g. the QR link a participant gets back to their
  // own approved bird. When present we skip the text IntroSequence (but keep
  // the Enter gate so its tap unlocks audio autoplay) and, on the gate press,
  // auto-focus that story's bird in the digital twin. Read once at mount.
  const [deepLinkStoryId] = useState<string | undefined>(() =>
    typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("story") ?? undefined : undefined,
  );
  // Set inside the Enter-gate press (the user gesture) so the twin's one-shot
  // deeplink focus fires with audio unlocked. Undefined in the normal flow.
  const [armedFocusId, setArmedFocusId] = useState<string | undefined>(undefined);
  // "Enter the Murmuration" preload gate. Shown on every page load and
  // dismissed only by its CTA. Skipped when arriving on a #record deep link
  // (the AR-exit record flow) so the record sheet isn't buried behind it.
  const [entered, setEntered] = useState(() => typeof window !== "undefined" && window.location.hash === "#record");
  // Text-only intro sequence between the gate and the live twin. Mounted on
  // the gate CTA *press* (so it sits under the gate's 450ms fade — no flash
  // of the twin between the two). Skipped on the #record deep link, same as
  // the gate itself.
  const [introStarted, setIntroStarted] = useState(false);
  const [introDone, setIntroDone] = useState(
    () => typeof window !== "undefined" && (window.location.hash === "#record" || new URLSearchParams(window.location.search).has("story")),
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  // First click/keypress anywhere — dismisses the "Select a bird" hint.
  // (The lockup itself never fades — sprint feedback 2026-06-11.)
  const [hasInteracted, setHasInteracted] = useState(false);
  const [storyActive, setStoryActive] = useState(false);
  // Flock loaded + stories resolved — gates the "Select a bird" hint so it
  // doesn't overlap the loading pill (both sit dead-centre).
  const [twinReady, setTwinReady] = useState(false);
  // Subtitle visibility toggle — controls the CC icon's pressed state.
  // DigitalTwinView reads this via the data-cc body attribute set below.
  const [ccEnabled, setCcEnabled] = useState(false);
  const armedListenersRef = useRef<((e: Event) => void) | null>(null);
  const { step: onboardingStep, advance: advanceOnboarding } = useOnboarding();

  // Decode-gate the below-fold ms-about murmuration video: only decode it while
  // it's actually on-screen and no story is playing, so it never competes with
  // the story-audio decoder while the user is up in the hero (iOS freeze fix).
  const aboutVideoRef = useRef<HTMLVideoElement | null>(null);
  const [reduceMotion] = useState(() => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  useDecodeGatedVideo(aboutVideoRef, MurmurationVideo, storyActive, !reduceMotion);

  // Intro music — started by the EnterSplash CTA tap (the user gesture, so
  // autoplay-safe), looping for the life of the microsite. Owned here rather
  // than by IntroSequence so it survives the intro's unmount: it plays at
  // full level under the intro, then ducks to a quiet bed under the twin.
  const introAudioRef = useRef<HTMLAudioElement | null>(null);
  // The loop is routed through a Web Audio GainNode for volume control,
  // because iOS Safari ignores writes to HTMLMediaElement.volume (it's pinned
  // at 1.0) — so every duck/dip below was a silent no-op on iOS, leaving the
  // bed blaring at full level. Gain is applied inside the audio graph and
  // *does* respond on iOS. Same pattern StoryPlayer.ts uses for the bird voice
  // (which is why voices play in the twin on iOS). audioDuckRaf is only used
  // by the non-Web-Audio fallback path.
  const audioCtxRef = useRef<AudioContext | null>(null);
  const musicGainRef = useRef<GainNode | null>(null);
  const audioDuckRaf = useRef<number | null>(null);

  // iOS Safari plays exactly ONE audio stream at a time (Apple: "all devices
  // running iOS are limited to playback of a single audio or video stream at
  // any time"). With the music bed and the story voice as TWO separate audio
  // clients (the music's AudioContext + a bare voice <audio> element) iOS made
  // them contend — and freeing the slot via an async ctx.suspend() raced the
  // in-gesture voice play() ("story sometimes won't play; revisit works"), while
  // the suspend/resume dance also stranded the bed silent ("music stops"). The
  // fix: on iOS, route the voice into the SAME AudioContext as the music (see
  // connectVoice) so iOS sees ONE mixed stream — nothing to suspend, no race,
  // and the music simply ducks under the voice (the storyActive effect below,
  // now on all platforms). Android/desktop mix freely, so the voice stays bare
  // there (proven; no Web Audio). The music already proves this graph works on
  // iOS — it's routed element→MediaElementSource→gain→destination for volume.
  const isIOS = useMemo(() => {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    return /iP(hone|ad|od)/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  }, []);

  // Restore the music bed after the page was hidden: resume the context and/or
  // restart the paused element. The element/context are only paused/suspended by
  // the visibilitychange handler now (no per-story suspend), plus an iOS
  // audio-session interruption (call / route change) can suspend the context —
  // re-running this on the next visibilitychange or pointerdown (a gesture
  // context, where iOS reliably honours resume/play) recovers both.
  const restoreMusic = useCallback(() => {
    const ctx = audioCtxRef.current;
    const audio = introAudioRef.current;
    if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
    if (audio && audio.paused) audio.play().catch(() => {});
  }, []);

  // Route the story-voice element into the music's AudioContext (iOS only) so
  // the two share one output stream. Called once with the player's element when
  // it's created (StoryPlayer.setElementRouter → FlockInteractionController →
  // DigitalTwinView onConnectVoice). Bare on Android/desktop (no contention to
  // solve). If the context isn't built yet (shouldn't happen — it's created at
  // the Enter gate, well before the first bird tap) we leave the element bare.
  // createMediaElementSource throws if called twice on an element; StoryPlayer
  // guards one-call-per-element and catches a throw (→ bare fallback).
  const connectVoice = useCallback(
    (el: HTMLAudioElement) => {
      if (!isIOS) return;
      const ctx = audioCtxRef.current;
      if (!ctx) return;
      const src = ctx.createMediaElementSource(el);
      const g = ctx.createGain();
      g.gain.value = 1; // voice at full; the music ducks around it
      src.connect(g).connect(ctx.destination);
    },
    [isIOS],
  );

  const startIntroAudio = useCallback((volume: number = INTRO_MUSIC_VOLUME) => {
    const audio = new Audio(IntroMusic);
    audio.loop = true;
    // In the DOM (hidden): iOS gives in-document media elements foreground load
    // priority. A detached new Audio() can stall its fetch on iOS the same way
    // the story element did ("waiting rs0 ns2") — a likely contributor to the
    // music "jamming". Removed on unmount below.
    audio.setAttribute("playsinline", "");
    audio.style.display = "none";
    if (typeof document !== "undefined" && document.body) document.body.appendChild(audio);
    introAudioRef.current = audio;

    // We're inside the gate-press gesture, so creating/resuming the context is
    // autoplay-safe. On success the GainNode owns the level and the element
    // stays at its default volume (1.0); on failure we fall back to setting
    // element volume directly — a non-iOS safety net, since the graph above is
    // exactly what makes ducking work on iOS.
    try {
      const Ctx = window.AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) throw new Error("no Web Audio");
      const ctx: AudioContext = audioCtxRef.current ?? new Ctx();
      audioCtxRef.current = ctx;
      ctx.resume().catch(() => {});
      // iOS suspends an AudioContext on ANY audio-session interruption — a story
      // <audio> element starting, Siri, an incoming call, a Bluetooth/headphone
      // route change — and does NOT auto-resume. Previously the only resumes
      // were visibilitychange + the per-story duck ramp, so an interruption
      // while the page stayed visible silenced the music "sometimes" with no
      // recovery. Auto-resume whenever it suspends while visible.
      ctx.onstatechange = () => {
        // Auto-resume if iOS suspended the context after an interruption (Siri,
        // call, route change) while the page is visible. With the unified graph
        // there's no per-story suspend to fight — the context stays running
        // during a story (the voice rides it), so this only catches real
        // interruptions.
        if (ctx.state === "suspended" && typeof document !== "undefined" && !document.hidden) {
          ctx.resume().catch(() => {});
          introAudioRef.current?.play().catch(() => {});
        }
      };
      const source = ctx.createMediaElementSource(audio);
      const gain = ctx.createGain();
      gain.gain.value = volume;
      source.connect(gain).connect(ctx.destination);
      musicGainRef.current = gain;
    } catch {
      musicGainRef.current = null;
      audio.volume = volume;
    }

    audio.play().catch(() => {
      /* Autoplay rejection (shouldn't happen off a tap) — stay silent. */
    });
  }, []);

  // Ramp the music volume toward `target` over `durationMs`, cancelling any
  // in-flight ramp so the latest request wins (the intro→twin duck and the
  // per-story dip share this). Prefers the Web Audio gain param (works on iOS);
  // falls back to a rAF ramp on element volume when the graph is unavailable.
  const rampMusicTo = useCallback((target: number, durationMs: number) => {
    const gain = musicGainRef.current;
    const ctx = audioCtxRef.current;
    if (gain && ctx) {
      ctx.resume().catch(() => {});
      const now = ctx.currentTime;
      // Start the ramp from the gain's LIVE value. While a previous ramp is
      // still in flight (e.g. tapping a bird mid intro→ambient duck, or a rapid
      // Next within STORY_DUCK_MS), `gain.gain.value` can return the stale
      // ramp-START value rather than the current interpolated one in some
      // engines, so cancelScheduledValues + setValueAtTime(value) would snap to
      // that stale value first → an audible jump/pop. cancelAndHoldAtTime
      // freezes the live interpolated value at `now` (leaving an implicit hold
      // event the ramp starts from), so the new ramp continues smoothly. Falls
      // back to the snap path where it's unsupported (older Firefox; the iOS/
      // Chrome targets here support it — and on iOS this path only runs once
      // anyway, since the per-story duck is gated off on iOS).
      const param = gain.gain as AudioParam & { cancelAndHoldAtTime?: (t: number) => void };
      if (typeof param.cancelAndHoldAtTime === "function") {
        param.cancelAndHoldAtTime(now);
      } else {
        param.cancelScheduledValues(now);
        param.setValueAtTime(param.value, now);
      }
      gain.gain.linearRampToValueAtTime(target, now + Math.max(durationMs, 0) / 1000);
      return;
    }
    const audio = introAudioRef.current;
    if (!audio) return;
    if (audioDuckRaf.current !== null) {
      cancelAnimationFrame(audioDuckRaf.current);
      audioDuckRaf.current = null;
    }
    const from = audio.volume;
    const start = performance.now();
    const step = (now: number) => {
      const k = durationMs > 0 ? Math.min((now - start) / durationMs, 1) : 1;
      audio.volume = from + (target - from) * k;
      audioDuckRaf.current = k < 1 ? requestAnimationFrame(step) : null;
    };
    audioDuckRaf.current = requestAnimationFrame(step);
  }, []);

  // Ramp the loop down to the ambient bed across the intro→twin crossfade.
  const duckIntroAudio = useCallback(() => rampMusicTo(AMBIENT_MUSIC_VOLUME, MUSIC_DUCK_MS), [rampMusicTo]);

  // Dip the bed further while a bird's story plays, then bring it back up to
  // the ambient level when the flock returns. `storyActive` is driven by the
  // twin (true whenever a bird is focused/playing). The mount run is a no-op:
  // either no audio exists yet (gate not pressed → rampMusicTo bails) or it's
  // the deeplink bed already at ambient, so the intro duck is never disturbed.
  useEffect(() => {
    // Duck the bed under the voice on ALL platforms. iOS used to be special-
    // cased (pause+suspend) to free the single audio slot; now the voice rides
    // the SAME context (see connectVoice), so iOS sees one mixed stream and a
    // plain gain duck keeps the bed quietly under the voice — same as Android/
    // desktop. The mount run is a no-op (no audio/ctx yet → rampMusicTo bails).
    rampMusicTo(storyActive ? FOCUSED_MUSIC_VOLUME : AMBIENT_MUSIC_VOLUME, STORY_DUCK_MS);
  }, [storyActive, rampMusicTo]);

  // Pause the loop while the page is hidden (tab switch / app backgrounded)
  // and resume when it returns — otherwise the bed keeps playing in the
  // background, most noticeably on iOS. Inert until the gate press starts the
  // loop (guarded on introAudioRef). Resume play() may be blocked off-gesture
  // on iOS; we attempt it and ignore rejection.
  useEffect(() => {
    const onVisibility = () => {
      const audio = introAudioRef.current;
      if (!audio) return;
      if (document.hidden) {
        audio.pause();
        audioCtxRef.current?.suspend().catch(() => {});
      } else {
        restoreMusic();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [restoreMusic]);

  // Belt-and-suspenders for the music stall: any tap revives the bed if iOS left
  // the context suspended (or the element paused) after an interruption. A
  // gesture-backed resume is the most reliable on iOS, and restoreMusic is a
  // cheap no-op when the bed is already running. Inert until the gate press
  // creates the context.
  useEffect(() => {
    const onPointer = () => restoreMusic();
    document.addEventListener("pointerdown", onPointer);
    return () => document.removeEventListener("pointerdown", onPointer);
  }, [restoreMusic]);

  // Stop the loop + tear down the audio graph when the microsite unmounts
  // (e.g. launching into AR).
  useEffect(
    () => () => {
      if (audioDuckRaf.current !== null) cancelAnimationFrame(audioDuckRaf.current);
      const introAudio = introAudioRef.current;
      if (introAudio) {
        introAudio.pause();
        // Drop the underlying media resource promptly (matches StoryPlayer.
        // dispose + useDecodeGatedVideo). Use removeAttribute('src') — NOT
        // src='', which resolves to the document base URL and kicks a spurious
        // load/error on the element (WebKit bug 42001).
        introAudio.removeAttribute("src");
        introAudio.load();
        introAudio.remove();
      }
      introAudioRef.current = null;
      musicGainRef.current = null;
      audioCtxRef.current?.close().catch(() => {});
      audioCtxRef.current = null;
    },
    [],
  );

  // Warm the intro's cloud video while the gate is up so it's (mostly)
  // buffered before the IntroSequence mounts — its own <video> then reads
  // from the HTTP cache. Dropped once the intro finishes (or on the #record
  // deep link, where introDone starts true).
  useEffect(() => {
    if (introDone) return;
    const warm = document.createElement("video");
    warm.preload = "auto";
    warm.muted = true;
    warm.src = BgIntroVideo;
    warm.load();
    return () => {
      warm.removeAttribute("src");
      warm.load();
    };
  }, [introDone]);

  // Detect touch class via pointer:coarse. Drives AR FAB shape (button vs QR
  // popover) AND record sheet placement (bottom sheet vs centered modal).
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(pointer: coarse)");
    const update = () => setIsMobile(mql.matches);
    update();
    mql.addEventListener?.("change", update);
    return () => mql.removeEventListener?.("change", update);
  }, []);

  // Narrow viewports (including shrunken desktop windows) collapse the
  // Launch AR pill to the circular button so it can't overlap the centred
  // record pill, and hide secondary controls during playback.
  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(max-width: 540px)");
    const update = () => setIsNarrow(mql.matches);
    update();
    mql.addEventListener?.("change", update);
    return () => mql.removeEventListener?.("change", update);
  }, []);

  // Open record sheet on initial hash, and on subsequent hash changes (AR exit
  // path writes `#record` then unmounts the AR overlay — when the microsite
  // mounts the hash will already be present).
  useEffect(() => {
    const checkHash = () => {
      if (window.location.hash === "#record") {
        setRecordOpen(true);
      }
    };
    checkHash();
    window.addEventListener("hashchange", checkHash);
    return () => window.removeEventListener("hashchange", checkHash);
  }, []);

  // First interaction dismisses the "Select a bird" hint. Held back until
  // the EnterSplash gate AND the intro sequence are done so neither the CTA
  // tap nor a Skip tap counts.
  useEffect(() => {
    if (!entered || !introDone || hasInteracted) return;
    // The pointer event that re-showed the hero may still be in flight — wait
    // a tick before arming the dismiss listener so we don't immediately close
    // on the very same gesture.
    const armTimer = window.setTimeout(() => {
      const dismiss = () => setHasInteracted(true);
      window.addEventListener("pointerdown", dismiss, { once: true });
      window.addEventListener("keydown", dismiss, { once: true });
      // store on the ref so cleanup can detach
      armedListenersRef.current = dismiss;
    }, 50);
    return () => {
      window.clearTimeout(armTimer);
      if (armedListenersRef.current) {
        window.removeEventListener("pointerdown", armedListenersRef.current);
        window.removeEventListener("keydown", armedListenersRef.current);
        armedListenersRef.current = null;
      }
    };
  }, [entered, introDone, hasInteracted]);

  // Lock body scroll while any sheet is open so the page can't move under
  // the scrim (and to avoid iOS rubber-banding behind the sheet).
  // Deterministic set/clear — NOT save/restore: a captured `prev` can be
  // poisoned by another lock holder (the EnterSplash gate locks scroll at
  // mount, so `prev` would capture "hidden" and re-apply it forever after
  // the first open/close cycle).
  useEffect(() => {
    if (!(menuOpen || recordOpen)) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen, recordOpen]);

  // Mirror CC state to a document body data attribute so DigitalTwinView (and
  // any future subtitle consumers) can react with a CSS rule rather than a
  // prop drill.
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (ccEnabled) {
      document.body.removeAttribute("data-cc-off");
    } else {
      document.body.setAttribute("data-cc-off", "true");
    }
    // Don't leak the attribute past unmount (e.g. into the AR surface,
    // which manages its own CC state).
    return () => document.body.removeAttribute("data-cc-off");
  }, [ccEnabled]);

  // Esc closes the most recently-opened sheet.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (recordOpen) {
        closeRecord();
      } else if (qrOpen) {
        setQrOpen(false);
      } else if (menuOpen) {
        setMenuOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordOpen, qrOpen, menuOpen]);

  const closeRecord = useCallback(() => {
    setRecordOpen(false);
    // Clear `#record` from the URL so we don't immediately re-open if the
    // user closes the sheet without changing pages.
    if (window.location.hash === "#record") {
      try {
        const u = new URL(window.location.href);
        u.hash = "";
        window.history.replaceState({}, "", u.pathname + u.search);
      } catch {
        window.location.hash = "";
      }
    }
  }, []);

  const openRecord = useCallback(() => {
    advanceOnboarding(OnboardingStep.RECORD);
    setMenuOpen(false);
    setRecordOpen(true);
  }, [advanceOnboarding]);

  const handleLaunchAR = useCallback(() => {
    trackAnalyticsEvent(AnalyticsEvents.SELECT_CONTENT, {
      content_type: "cta",
      item_id: "launch_ar",
      item_name: "Launch AR",
    });
    setQrOpen(false);
    setMenuOpen(false);
    onLaunchAR();
  }, [onLaunchAR]);

  const handleBrandClick = useCallback(() => {
    setHasInteracted(false);
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  const arShareUrl = typeof window !== "undefined" ? window.location.origin + window.location.pathname : "";

  return (
    <div className="ms-shell">
      {/* BELOW-FOLD BACKDROP — fixed fullscreen cloud loop revealed through
          the transparent .ms-watercolor bands. First child so every
          positioned section (hero, bands, partners, footer) paints over it. */}
      <BelowFoldBackdrop storyActive={storyActive} />

      {/* HERO — viewport-sized panel containing the canvas and all
          canvas-anchored UI. Scrolls away as a unit when the user moves
          past the fold. */}
      <section className="ms-hero">
        {/* FLOCK CANVAS — fills the hero. */}
        <div className="ms-hero__canvas">
          {/* If the twin's chunk fails to load, the hero keeps its bg-hero.webp
              sky + all CTAs rather than white-screening the microsite. */}
          <LazyBoundary label="digital-twin">
            <Suspense fallback={null}>
              <DigitalTwinView
                queue={queue}
                focusStoryId={armedFocusId}
                className="dt-viewer"
                onStoryActiveChange={setStoryActive}
                onReadyChange={setTwinReady}
                onConnectVoice={connectVoice}
              />
            </Suspense>
          </LazyBoundary>
        </div>

        {/* SOFT HERO TITLE — wordmark SVG (per CLAUDE.md decision #11; no
            display serif webfont). Subtitle copy locked to "Stories of
            Migration in LA" to match Figma frames 1200/2876. Fades out on
            first interaction or 6s timeout. */}
        <div className="ms-hero-title">
          <img src={Wordmark} alt="Murmurations" className="ms-hero-title__wordmark" />
          <p className="ms-hero-title__tagline">Stories of Migration in LA</p>
        </div>

        {/* "Select a bird in the flock" hint — sits over the flock,
            non-interactive. Held back until the flock is interactive (the
            loading pill owns the centre until then); dismissed by the first
            click only. */}
        <div className={`ms-hint${twinReady && !hasInteracted ? " is-visible" : ""}`} aria-hidden="true">
          <span className="ms-hint__pill">
            Select a bird in the flock
            <HandIcon className="ms-hint__hand" />
          </span>
        </div>

        {/* RECORD CTA — first-class entry point. Bottom-center when nothing's
            playing; collapses to an icon circle above the CC button while a
            story plays. Hidden during playback on phones — the phone playback
            frames (3954/4154) reserve the bottom row for CC + nav only. */}
        {!recordOpen && !(storyActive && (isMobile || isNarrow)) && (
          <button
            type="button"
            onClick={openRecord}
            className={`ms-record-fab cta-pulse-ring${storyActive ? " ms-record-fab--side" : ""}`}
            aria-label="Record your story"
          >
            {!storyActive && <span>Record your story</span>}
            <MicIcon width={16} height={25} />
          </button>
        )}

        {/* AR LAUNCH FAB — bottom right of hero. Desktop: "Launch AR" pill
            with QR glyph, opening the QR popover (frame 1304). Mobile: a
            compact circular "AR" button (frame 2876) that stays up during
            playback — the 1.1 playback frames (2648:9174) keep it as the
            right anchor of the bottom row. */}
        <ArLaunchFab
          isMobile={isMobile}
          compact={isMobile || isNarrow}
          qrOpen={qrOpen}
          setQrOpen={setQrOpen}
          arShareUrl={arShareUrl}
          onLaunchAR={handleLaunchAR}
        />

        {/* CC button — bottom-left of hero per Figma frames 1200/2876.
            Toggles subtitle visibility on the digital twin via the
            data-cc-off body attribute (see the effect above + style.scss). */}
        <button
          type="button"
          onClick={() => setCcEnabled((v) => !v)}
          aria-pressed={ccEnabled}
          aria-label={ccEnabled ? "Hide subtitles" : "Show subtitles"}
          className={`ms-cc${ccEnabled ? "" : " is-off"}`}
        >
          <CcIcon width={22} height={18} />
          {/* Slash overlay while off — mirrors the AR overlay's CC toggle. */}
          {!ccEnabled && (
            <svg
              className="ms-cc__slash"
              width="28"
              height="28"
              viewBox="0 0 28 28"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M4 24 L24 4" />
            </svg>
          )}
        </button>

        {/* RECORD tooltip — points at the record FAB. Anchors mirror the
            FAB's exact SCSS baselines ($fab-bottom / $fab-height) plus the
            13px pulse-ring reach, so the notch sits just above the ring at
            every viewport. Hidden while the record sheet or menu is open. */}
        <OnboardingTooltip
          visible={
            onboardingStep === OnboardingStep.RECORD &&
            !recordOpen &&
            !menuOpen &&
            // The record FAB hides during phone playback — don't point at it.
            !(storyActive && (isMobile || isNarrow))
          }
          text="Record your own story"
          anchor={
            storyActive
              ? {
                  // Side-collapsed FAB (desktop playback): one slot above CC.
                  left: "clamp(16px, 3vw, 60px)",
                  bottom: "calc(env(safe-area-inset-bottom, 0px) + clamp(16px, 5vh, 60px) + clamp(46px, 5vw, 54px) * 2 + 16px + 21px)",
                }
              : {
                  left: "50%",
                  transform: "translateX(-50%)",
                  bottom: "calc(env(safe-area-inset-bottom, 0px) + clamp(16px, 5vh, 60px) + clamp(46px, 5vw, 54px) + 21px)",
                }
          }
          arrow="bottom"
          arrowOffset={storyActive ? "26px" : "50%"}
          autoAdvanceMs={7000}
          onAutoAdvance={() => advanceOnboarding(OnboardingStep.RECORD)}
        />
      </section>

      {/* ── BELOW THE FOLD ─────────────────────────────────────────────── */}

      {/* Editorial body (about → credits) in one relative wrapper so the
          large bird flocks can overlay section boundaries — they live in a
          single layer rendered after the sections (see the end of this
          wrapper) per nodes 2521:1529-1560. */}
      <div className="ms-below-fold">
        {/* Watercolor band 1 — About + venue photo on the bg.webp wash with
            white copy, per frame 2532. */}
        <div className="ms-watercolor">
          <section className="ms-about" id="about">
            <div className="ms-column">
              <p className="ms-section-heading">About the project</p>
              <div className="ms-prose">
                <p className="ms-prose__para">All of nature migrates, yet only human migration is criminalized.</p>
                <p className="ms-prose__para">
                  Murmurations is a civic storytelling and public art project inspired by the collective flight of starlings, birds that move together for
                  protection. In this work, each "bird" carries the story of a person's response to ICE raids in Los Angeles, reflecting a wide range of lived
                  experiences across the community. The public is also invited to share audio reflections, which are woven into a living flock in augmented
                  reality.
                </p>
                <p className="ms-prose__para ms-prose__para--last">
                  At Gloria Molina Grand Park, visitors can scan a QR code and point their devices toward City Hall to reveal a flock in the sky. Tapping a bird
                  reveals a story. Together, these voices create a living public archive and a shared call for dignity, belonging, and the right for families to
                  stay together.
                </p>
              </div>

              {/* src + play/pause are owned by useDecodeGatedVideo (above) so
                  this off-screen loop only holds a decoder when scrolled into
                  view. Poster (City Hall) shows until then / for reduced-motion. */}
              <video
                ref={aboutVideoRef}
                className="ms-photo"
                poster={CityHallPoster}
                muted
                loop
                playsInline
                preload="none"
                aria-label="Murmurations flying around in AR at City Hall"
              />
            </div>
          </section>
        </div>

        {/* TESTIMONIALS — fixed-height dark band with quote carousel + dots. */}
        <TestimonialsSection />

        {/* Watercolor band 2 — Resources + Know Your Rights + Credits. */}
        <div className="ms-watercolor">
          <section className="ms-resources" id="resources">
            <div className="ms-column">
              <p className="ms-section-heading">Resources</p>
              <div className="ms-prose">
                <p className="ms-prose__para ms-prose__para--last">
                  Murmurations calls us to action to help protect our loved ones, our neighbours, and ourselves. By sharing this link and printing the{" "}
                  <a href="https://www.ilrc.org" className="ms-link" target="_blank" rel="noopener noreferrer">
                    Immigrant Legal Resource Center
                  </a>{" "}
                  multilingual Know Your Rights cards, anyone can help protect immigrant communities. Join us in building a future where every family has the
                  freedom to stay together, to thrive, and to be there for the moments that matter.
                </p>
              </div>
              <div className="ms-kyr">
                <p className="ms-section-heading">Know your rights</p>
                <p className="ms-prose__para ms-prose__para--last">
                  Download Know Your Rights Cards {/* TODO(Liam): placeholder href — swap for the real download URL. */}
                  <a href="https://www.ilrc.org/redcards" className="ms-link" target="_blank" rel="noopener noreferrer">
                    here
                  </a>{" "}
                  in 19 languages.
                </p>
              </div>
            </div>
          </section>

          {/* CREDITS — three columns, node range 2501:2708–2501:2726. */}
          <section className="ms-credits" id="credits">
            <div className="ms-column">
              <p className="ms-section-heading">Credits</p>
              <div className="ms-credits__grid">
                <div className="ms-credits__column">
                  <p className="ms-credits__line">
                    <strong className="font-black">Executive Producer/Director:</strong> Sutu
                  </p>
                  <p className="ms-credits__line">
                    <strong className="font-black">Executive Producer:</strong> Michelle Woo
                  </p>
                  <p className="ms-credits__line">
                    <strong className="font-black">Director / Producer:</strong> Tani Ikeda
                  </p>
                  <p className="ms-credits__line">
                    <strong className="font-black">Consulting Producer:</strong> Kamal Sinclair
                  </p>
                  <p className="ms-credits__line">
                    <strong className="font-black">Producer:</strong> Ali Lucker
                  </p>
                </div>
                <div className="ms-credits__column">
                  <p className="ms-credits__line">
                    <strong className="font-black">Producer:</strong> Alexandra Neville
                  </p>
                  <p className="ms-credits__line">
                    <strong className="font-black">Associate Producer:</strong> Ting Y. Lin
                  </p>
                  <p className="ms-credits__line">
                    <strong className="font-black">Visual Artist:</strong> Erin Yoshi
                  </p>
                  <p className="ms-credits__line">
                    <strong className="font-black">Graphic Designer:</strong> Mif
                  </p>
                  <p className="ms-credits__line">
                    <strong className="font-black">Chief Technology Officer:</strong> Lukasz Karluk
                  </p>
                </div>
                <div className="ms-credits__column">
                  <p className="ms-credits__line">
                    <strong className="font-black">Technical Director:</strong> Jack Gerrard
                  </p>
                  <p className="ms-credits__line">
                    <strong className="font-black">Front End Developer:</strong> Liam Hunt
                  </p>
                  <p className="ms-credits__line">
                    <strong className="font-black">Composer:</strong> Mikael Tobias
                  </p>
                  <p className="ms-credits__line">
                    <strong className="font-black">Special Thanks:</strong> Naomi Suzuki
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* BIRD OVERLAYS — large edge flocks spanning section boundaries
            (nodes 2521:1560/1299/1546/1554/1529). Painted after the sections
            so they overlap both the watercolor bands and the dark testimonial
            band. Non-interactive. */}
        <div className="ms-birds" aria-hidden="true">
          <img src={BirdsLeftTall} alt="" className="ms-birds__left-tall" />
          <img src={BirdsTrio} alt="" className="ms-birds__trio" />
          <img src={BirdsRightLarge} alt="" className="ms-birds__right-large" />
          <img src={BirdsLeftWide} alt="" className="ms-birds__left-wide" />
          <img src={BirdsRightTall} alt="" className="ms-birds__right-tall" />
        </div>
      </div>

      {/* PARTNERS — #28104C band (node 2501:2534): left-aligned heading over
          an even 4×2 logo grid (2 columns on small screens via media query). */}
      <section className="ms-partners" id="partners" aria-label="Partners">
        <div className="ms-partners__container">
          <p className="ms-section-heading">Partners</p>
          <div className="ms-partners__grid">
            {[
              { src: AgogLogo, alt: "agog: The Immersive Media Institute" },
              { src: EyejackLogo, alt: "Eyejack" },
              { src: ForFreedomsLogo, alt: "For Freedoms" },
              { src: MusicCenterLogo, alt: "The Music Center" },
              { src: JanmLogo, alt: "Japanese American National Museum" },
              { src: ChirlaLogo, alt: "CHIRLA" },
              { src: CleanLogo, alt: "CLEAN Carwash Worker Center" },
              { src: GrandParkLogo, alt: "Gloria Molina Grand Park" },
            ].map((logo) => (
              <div key={logo.alt} className="ms-partners__cell">
                <img src={logo.src} alt={logo.alt} className="ms-partners__logo" loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="ms-footer">
        <p>© 2026 Murmurations · Los Angeles</p>
      </footer>

      {/* TOP OVERLAY BAND — pinned to viewport. Per Figma frames 1200/2876,
          this is hamburger-only (top-right); the wordmark is part of the hero
          fade-out group, not a persistent brand pill. handleBrandClick is
          still wired to the hidden Back-to-Top affordance below. */}
      <header className="ms-top-band">
        <span aria-hidden="true" />
        <button
          type="button"
          className="ms-menu-button"
          onClick={() => setMenuOpen(true)}
          aria-label="Open menu"
          aria-haspopup="dialog"
          aria-expanded={menuOpen}
        >
          <svg width="50" height="28" viewBox="0 0 50 28" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M0 1h50M0 14h50M0 27h50" />
          </svg>
        </button>
      </header>

      {/* Visually-hidden Back-to-top button — keyboard/screen-reader path
          back to the hero (the visible header is hamburger-only). */}
      <button type="button" onClick={handleBrandClick} aria-label="Back to top" className="visually-hidden" />

      {/* MENU SHEET — slide-in from right. On desktop the CTA opens the QR
          popover inside the menu (the only desktop path into AR is scanning
          the code). */}
      <MenuSheet open={menuOpen} onClose={() => setMenuOpen(false)} onLaunchAR={handleLaunchAR} isMobile={isMobile} arShareUrl={arShareUrl} />

      {/* RECORD SHEET — bottom sheet (touch) / centered modal (pointer-fine). */}
      <RecordSheet open={recordOpen} onClose={closeRecord} isMobile={isMobile} />

      {/* INTRO SEQUENCE — text-only animation between the gate and the live
          twin. Mounts under the gate's fade-out, crossfades into the flock. */}
      {introStarted && !introDone && <IntroSequence onFadeBegin={duckIntroAudio} onComplete={() => setIntroDone(true)} />}

      {/* ENTER GATE — City Hall preload splash over everything, every page
          load. The twin keeps loading behind it. Its CTA press is the user
          gesture that starts the intro music loop (synchronous — iOS Safari
          refuses play() outside the gesture's call stack). */}
      {!entered && (
        <EnterSplash
          onEnter={() => setEntered(true)}
          onEnterPressed={() => {
            // Deeplink: arm the twin's one-shot bird focus inside the gesture
            // (so the story audio is autoplay-unlocked) and skip the intro
            // music + sequence. Suppress the "Select a bird" hint since we're
            // auto-selecting. Normal flow: start intro music + sequence.
            if (deepLinkStoryId) {
              setArmedFocusId(deepLinkStoryId);
              setHasInteracted(true);
              // No intro to ramp down from — start the loop straight at the
              // ambient bed level so the deeplink still has background music
              // under the story playback.
              startIntroAudio(AMBIENT_MUSIC_VOLUME);
            } else {
              startIntroAudio();
              setIntroStarted(true);
            }
          }}
        />
      )}

      {/* On-device diagnostics (?debug) — build id + live media-decoder census
          + twin fps, persisted across an OOM reload. Inert without ?debug. */}
      {perfHudEnabled() && <PerfHud />}
    </div>
  );
}

// ── Below-fold backdrop ────────────────────────────────────────────────────
function BelowFoldBackdrop({ storyActive }: { storyActive: boolean }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [reduceMotion] = useState(() => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);

  // Decode-gate on the video element itself: it only holds a decoder while it's
  // on screen AND no story is playing, and we tear down src off-screen so the
  // hardware decoder + buffer are freed (not just paused). At the top of the
  // page the hero covers this entirely, so it never competes with the story
  // audio decoder. preload="none" so the ~2MB clip isn't resident up in the
  // hero. This video is position:fixed (always intersecting), so gate on the
  // scrollable .ms-below-fold wrapper entering the viewport — the same target
  // the original IntersectionObserver watched.
  useDecodeGatedVideo(videoRef, BgCopyVideo, storyActive, !reduceMotion, () => document.querySelector(".ms-below-fold"));

  return (
    <div className="ms-bg-video" aria-hidden="true">
      {!reduceMotion && (
        <video
          ref={videoRef}
          className={playing ? "is-playing" : ""}
          muted
          loop
          playsInline
          preload="none"
          onPlaying={() => setPlaying(true)}
          onEmptied={() => setPlaying(false)}
        />
      )}
    </div>
  );
}

// ── AR Launch FAB ──────────────────────────────────────────────────────────

interface ArLaunchFabProps {
  isMobile: boolean;
  /** Render the circular "AR" button instead of the full pill (touch devices
   * and narrow viewports). */
  compact: boolean;
  qrOpen: boolean;
  setQrOpen: (v: boolean) => void;
  arShareUrl: string;
  onLaunchAR: () => void;
}

function ArLaunchFab({ isMobile, compact, qrOpen, setQrOpen, arShareUrl, onLaunchAR }: ArLaunchFabProps) {
  // Mobile: compact circular "AR" button → launches directly (frame 2876).
  // Desktop: "Launch AR" pill with QR glyph → toggles the QR popover; the
  // only desktop path into AR is scanning the code (no desktop WebXR).
  return (
    <div className="ms-ar-fab">
      {!isMobile && qrOpen && (
        <div className="ms-qr-popover" role="dialog" aria-label="Scan QR to launch AR on your phone">
          <div className="ms-qr-popover__inner">
            <QRCode value={arShareUrl} size={160} fgColor="#111" bgColor="#ffffff" />
          </div>
          <p className="ms-qr-popover__caption">Scan QR to launch on your phone</p>
          <span className="ms-qr-popover__notch" aria-hidden="true" />
        </div>
      )}
      <button
        type="button"
        onClick={isMobile ? onLaunchAR : () => setQrOpen(!qrOpen)}
        className={`ms-ar-fab__button${compact ? " ms-ar-fab__button--compact" : ""}`}
        aria-label={isMobile ? "Launch the AR experience" : "Show AR launch QR code"}
        aria-expanded={isMobile ? undefined : qrOpen}
      >
        {compact ? (
          <span className="ms-ar-fab__compact-label">AR</span>
        ) : (
          <>
            <span>Launch AR</span>
            <QrIcon width={20} height={18} />
          </>
        )}
      </button>
    </div>
  );
}

// ── Menu Sheet ─────────────────────────────────────────────────────────────

interface MenuSheetProps {
  open: boolean;
  onClose: () => void;
  /** Mobile only — desktop shows the in-menu QR popover instead. */
  onLaunchAR: () => void;
  isMobile: boolean;
  arShareUrl: string;
}

function MenuSheet({ open, onClose, onLaunchAR, isMobile, arShareUrl }: MenuSheetProps) {
  // Desktop CTA toggles a QR popover anchored to the menu's own pill —
  // routing to the hero FAB's popover would open it off-screen whenever the
  // page is scrolled below the fold.
  const [qrOpen, setQrOpen] = useState(false);
  useEffect(() => {
    if (!open) setQrOpen(false);
  }, [open]);

  return (
    <>
      {/* Light scrim — the sheet itself is translucent (rgba(27,7,58,0.9)),
          so the page should stay visible behind it rather than going black. */}
      <div className={`ms-scrim ms-scrim--light${open ? " is-open" : ""}`} onClick={onClose} aria-hidden="true" />
      <aside role="dialog" aria-modal="true" aria-label="Menu" aria-hidden={!open} className={`ms-menu${open ? " is-open" : ""}`}>
        <div className="ms-menu__header">
          <button type="button" onClick={onClose} className="ms-menu__close" aria-label="Close menu">
            <svg width="35" height="35" viewBox="0 0 36 36" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M1 1l34 34M35 1L1 35" />
            </svg>
          </button>
        </div>

        <div className="ms-menu__body">
          <section className="ms-menu__section">
            <h3 className="ms-menu__heading">Murmurations</h3>
            <p className="ms-menu__para">
              A public AR installation built in response to the ICE raids in Los Angeles. We invited Angelenos to record short stories about how they're feeling
              and what they hope for the city.
            </p>
            <p className="ms-menu__para">
              Each approved story becomes a bird in a flock of voices. Stand inside the murmuration and tap toward it — a single bird breaks away, flies to you,
              and plays its recording.
            </p>
          </section>

          <section className="ms-menu__section">
            <h3 className="ms-menu__heading">How it works</h3>
            <p className="ms-menu__para">
              On the microsite, click a bird to hear a story. Use ← / → to skip between voices, Esc to release the current bird back to the flock.
            </p>
            <p className="ms-menu__para">
              In AR, find a flat space, tap to place the murmuration, then tap toward it to draw out a story. Stories pass through human moderation before
              release; anonymous submissions receive a private deletion code, email submissions a QR deep-link back to their own bird once approved.
            </p>
          </section>

          <section className="ms-menu__section">
            <h3 className="ms-menu__heading">Support</h3>
            <p className="ms-menu__para">
              For questions about the project, technical support, or assistance managing a previously submitted story, contact{" "}
              <a href="mailto:info+murmurations@eyejackapp.com" className="ms-link">info+murmurations@eyejackapp.com</a>
            </p>
          </section>

          <section className="ms-menu__actions">
            {!isMobile && qrOpen && (
              <div className="ms-qr-popover ms-qr-popover--menu" role="dialog" aria-label="Scan QR to launch AR on your phone">
                <div className="ms-qr-popover__inner">
                  <QRCode value={arShareUrl} size={160} fgColor="#111" bgColor="#ffffff" />
                </div>
                <p className="ms-qr-popover__caption">Scan QR to launch on your phone</p>
                <span className="ms-qr-popover__notch ms-qr-popover__notch--left" aria-hidden="true" />
              </div>
            )}
            <button
              type="button"
              className="btn-ghost ms-menu__cta"
              onClick={isMobile ? onLaunchAR : () => setQrOpen((v) => !v)}
              aria-expanded={isMobile ? undefined : qrOpen}
            >
              <span>Launch AR</span>
              <QrIcon width={20} height={18} />
            </button>
          </section>

          <p className="ms-menu__footer">© 2026 Murmurations</p>
        </div>
      </aside>
    </>
  );
}

// ── Record Sheet ───────────────────────────────────────────────────────────

interface RecordSheetProps {
  open: boolean;
  onClose: () => void;
  isMobile: boolean;
}

function RecordSheet({ open, onClose, isMobile }: RecordSheetProps) {
  // Render the heavy RecordSection only when open — its Firebase + getUserMedia
  // setup shouldn't fire on initial landing.
  const variant = isMobile ? "ms-record-sheet--mobile" : "ms-record-sheet--desktop";
  return (
    <>
      <div className={`ms-scrim${open ? " is-open" : ""}`} onClick={onClose} aria-hidden="true" />
      <div role="dialog" aria-modal="true" aria-label="Record your story" aria-hidden={!open} className={`ms-record-sheet ${variant}${open ? " is-open" : ""}`}>
        <div className="ms-record-sheet__handle-row">
          <button type="button" onClick={onClose} className="ms-record-sheet__close" aria-label="Close record sheet">
            <CloseIcon />
          </button>
        </div>
        <div className="ms-record-sheet__body">{open && <RecordSection />}</div>
      </div>
    </>
  );
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <path d="M5 5l10 10M15 5L5 15" />
    </svg>
  );
}

// ── Testimonials ───────────────────────────────────────────────────────────

// Quote carousel per frame 2532: dark band, TESTIMONIALS eyebrow, one quote
// at a time with pager dots. The first quote is from the Figma frames; the
// other two are dummy copy holding the slots until Sutu supplies the real
// testimonials.
const TESTIMONIALS = [
  {
    quote: "They tried to break his spirit-but what I learned is that community is stronger than fear.",
    attribution: "Nadiya Carcana",
  },
  {
    quote: "Every person taken is a world to their community.",
    attribution: "Carla Orendorff",
  },
  {
    quote: "Why can't I just enjoy being a kid. I cannot even sleep or do my homework. All I can think about is my mother being taken away.",
    attribution: "Leah Sanles Wilson",
  },
];

function TestimonialsSection() {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const autoTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const current = TESTIMONIALS[index];

  // Crossfade to a slide: fade out, swap, fade in.
  const goTo = useCallback((i: number) => {
    setVisible(false);
    setTimeout(() => {
      setIndex(i);
      setVisible(true);
    }, 250);
  }, []);

  // Gentle auto-advance; any manual dot press restarts the clock.
  useEffect(() => {
    autoTimer.current = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex((i) => (i + 1) % TESTIMONIALS.length);
        setVisible(true);
      }, 250);
    }, 9000);
    return () => {
      if (autoTimer.current) clearInterval(autoTimer.current);
    };
  }, [index]);

  // Node 2501:2686: fixed 500px #28104C band; heading / quote / dots are a
  // centred column. The quote slot has a fixed height so longer quotes never
  // resize the band.
  return (
    <section className="ms-quote-band" id="testimonials">
      <p className="ms-section-heading ms-section-heading--center">Testimonials</p>
      <blockquote className="ms-quote">
        <img src={OpenQuote} alt="" aria-hidden="true" className="ms-quote__mark ms-quote__mark--open" />
        <div className={`ms-quote__slot${visible ? "" : " is-hidden"}`}>
          <p className="ms-quote__text">{current.quote}</p>
          <footer className="ms-quote__attr">{current.attribution}</footer>
        </div>
        <img src={CloseQuote} alt="" aria-hidden="true" className="ms-quote__mark ms-quote__mark--close" />
      </blockquote>
      <div className="ms-quote__dots" role="tablist" aria-label="Testimonials">
        {TESTIMONIALS.map((t, i) => (
          <button
            key={t.attribution + i}
            type="button"
            role="tab"
            aria-selected={i === index}
            aria-label={`Testimonial ${i + 1}`}
            onClick={() => goTo(i)}
            className={`ms-quote__dot${i === index ? " is-active" : ""}`}
          />
        ))}
      </div>
    </section>
  );
}
